import { useGestock } from "../GestockContext";
import { useCommandes, useJournal } from "../useOilmed";
import { fmt } from "../utils";

const TYPE_CONFIG = {
  "Entrée Douane": { icon: "🔒", color: "var(--gold)" },
  Dédouanement: { icon: "✅", color: "var(--blue)" },
  Commande: { icon: "📋", color: "var(--green)" },
  Livraison: { icon: "🚛", color: "var(--green)" },
  Vente: { icon: "⛽", color: "var(--red)" },
  SuppressionCommande: { icon: "🗑", color: "var(--red)" },
};

const PRODUCT_LABEL = {
  essence: "Super",
  go: "Go",
};

function toDateValue(value) {
  if (!value) return null;

  const date =
    value instanceof Date
      ? value
      : (value?.toDate?.() ??
        (typeof value === "string" ? new Date(value) : null));

  if (!date || Number.isNaN(date.getTime())) return null;
  return date;
}

function toIsoDate(value) {
  const date = toDateValue(value);
  if (!date) return null;
  return date.toISOString().slice(0, 10);
}

function normalizeProgramme(value) {
  const digits = String(value ?? "")
    .trim()
    .toUpperCase()
    .replace(/O/g, "0")
    .replace(/\D/g, "");

  return digits.replace(/^0+/, "") || digits;
}

function getStationBlPairs(entry) {
  const pairs = new Set();

  if (entry.station && entry.numBL) {
    pairs.add(`${String(entry.station).toUpperCase()}|${String(entry.numBL)}`);
  }

  if (Array.isArray(entry.lignes)) {
    entry.lignes.forEach((line) => {
      const station = line?.station || line?.stationId;
      const bl = line?.bl || line?.numBL;
      if (station && bl) {
        pairs.add(`${String(station).toUpperCase()}|${String(bl)}`);
      }
    });
  }

  if (entry.stations) {
    if (Array.isArray(entry.stations)) {
      entry.stations.forEach((line) => {
        const station = line?.station || line?.stationId;
        const bl = line?.bl || line?.numBL;
        if (station && bl) {
          pairs.add(`${String(station).toUpperCase()}|${String(bl)}`);
        }
      });
    } else if (typeof entry.stations === "object") {
      Object.entries(entry.stations).forEach(([stationId, stationData]) => {
        const bl = stationData?.bl || stationData?.numBL;
        if (bl) pairs.add(`${String(stationId).toUpperCase()}|${String(bl)}`);
      });
    }
  }

  return pairs;
}

function getCommandeCount(entry) {
  if (Array.isArray(entry.commandeIds) && entry.commandeIds.length > 0) {
    return entry.commandeIds.filter(Boolean).length;
  }

  if (Array.isArray(entry.lignes) && entry.lignes.length > 0) {
    return entry.lignes.length;
  }

  if (entry.stations) {
    if (Array.isArray(entry.stations)) {
      return entry.stations.reduce((total, line) => {
        const idsCount = Array.isArray(line?.commandeIds)
          ? line.commandeIds.length
          : 0;
        const blsCount = Array.isArray(line?.bls) ? line.bls.length : 0;
        return total + Math.max(idsCount, blsCount, 1);
      }, 0);
    }
    if (typeof entry.stations === "object") {
      return Object.values(entry.stations).reduce((total, stationData) => {
        const idsCount = Array.isArray(stationData?.commandeIds)
          ? stationData.commandeIds.length
          : 0;
        const blsCount = Array.isArray(stationData?.bls)
          ? stationData.bls.length
          : 0;
        return total + Math.max(idsCount, blsCount, 1);
      }, 0);
    }
  }

  return 1;
}

export default function GJournal() {
  const { monthRange } = useGestock();
  const { journal, loading } = useJournal(500, monthRange);
  const { commandes } = useCommandes();
  const commandeDatesById = new Map(commandes.map((cmd) => [cmd.id, cmd.date]));

  const getLinkedCommandeDateValue = (entry) => {
    const ids = [
      ...(Array.isArray(entry.commandeIds) ? entry.commandeIds : []),
      entry.commandeId,
    ].filter(Boolean);

    for (const id of ids) {
      const date = toDateValue(commandeDatesById.get(id));
      if (date) return date;
    }

    const entryProg = normalizeProgramme(entry.reference ?? entry.numProg);
    const stationBlPairs = getStationBlPairs(entry);
    const matchingDates = commandes
      .filter((cmd) => {
        if (entry.terminal && cmd.terminal !== entry.terminal) return false;
        if (entry.produit && cmd.produit !== entry.produit) return false;

        const cmdProg = normalizeProgramme(cmd.numProg);
        if (!entryProg || !cmdProg || entryProg !== cmdProg) return false;

        if (stationBlPairs.size === 0) return true;
        return stationBlPairs.has(
          `${String(cmd.station).toUpperCase()}|${String(cmd.numBL)}`,
        );
      })
      .map((cmd) => toDateValue(cmd.date))
      .filter(Boolean)
      .sort((a, b) => a.getTime() - b.getTime());

    return matchingDates[0] ?? null;
  };

  const getEntryDateValue = (entry) => {
    if (entry.type === "Commande") {
      return (
        toDateValue(entry.date) ??
        getLinkedCommandeDateValue(entry) ??
        toDateValue(entry.createdAt)
      );
    }

    return toDateValue(entry.date) ?? toDateValue(entry.createdAt);
  };

  const orderedJournal = [...journal].sort((a, b) => {
    const dateDelta =
      (getEntryDateValue(b)?.getTime() ?? 0) -
      (getEntryDateValue(a)?.getTime() ?? 0);

    if (dateDelta !== 0) return dateDelta;

    return (
      (toDateValue(b.createdAt)?.getTime() ?? 0) -
      (toDateValue(a.createdAt)?.getTime() ?? 0)
    );
  });

  return (
    <div className="fade-up">
      <div className="page-header">
        <div className="page-title">
          Journal <span>Mouvements</span>
        </div>
        <div className="page-sub">
          {"// TOUTES OPÉRATIONS — TOUS TERMINAUX"}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">📜 Historique complet</span>
          <span className="badge badge-blue">
            {orderedJournal.length} entrées
          </span>
        </div>
        {loading ? (
          <div className="empty">Chargement…</div>
        ) : orderedJournal.length === 0 ? (
          <div className="empty">Aucun mouvement enregistré</div>
        ) : (
          orderedJournal.map((e) => {
            const cfg = TYPE_CONFIG[e.type] ?? {
              icon: "📝",
              color: "var(--muted)",
            };

            // Format note based on type
            let displayNote = e.note;
            let displayMeta = [];

            if (e.type === "Commande") {
              const commandeDate = toIsoDate(getEntryDateValue(e)) ?? "—";

              // Check if consolidated (has stations) or individual
              if (e.stations && typeof e.stations === "object") {
                // Consolidated format: Programme with multiple stations
                displayNote = `Programme ${e.reference || "—"} — ${getCommandeCount(e)} commande(s)`;

                const volume = fmt(e.volumeM3 ?? e.quantite);
                const produit = e.produit === "essence" ? "SU" : "GO";
                const terminal = e.terminal;

                displayMeta = [commandeDate, volume, produit, terminal];
              } else {
                // Individual format (fallback)
                displayNote = e.note || `${e.reference || "—"}`;

                const blNumber = e.numBL || "—";
                const volume = fmt(e.volumeM3 ?? e.quantite);
                const produit = e.produit === "essence" ? "SU" : "GO";
                const terminal = e.terminal;

                displayMeta = [
                  commandeDate,
                  blNumber,
                  volume,
                  produit,
                  terminal,
                ];
              }
            } else if (e.type === "Vente") {
              // Vente format: Station - Product
              const station = e.stationLabel || e.station || "—";
              const produit = e.produit === "essence" ? "SU" : "GO";
              displayNote = `${station} - ${produit}`;

              const date = toIsoDate(e.date) ?? toIsoDate(e.createdAt) ?? "—";
              displayMeta = [date];
            } else {
              // Default format for other types (Entrée Douane, Dédouanement, Livraison)
              // Prefer event `date` over write timestamp `createdAt`
              const eventDate =
                toIsoDate(e.date) ?? toIsoDate(e.createdAt) ?? "—";
              displayMeta.push(eventDate);
              if (e.reference) displayMeta.push(e.reference);
              if (e.type === "Livraison" && e.produit) {
                displayMeta.push(PRODUCT_LABEL[e.produit] ?? e.produit);
              }
              if (e.terminal) displayMeta.push(e.terminal);
              if (e.camion && e.type !== "Livraison")
                displayMeta.push(`Camion: ${e.camion}`);
            }

            return (
              <div key={e.id} className="log-item">
                <div className="log-icon">{cfg.icon}</div>
                <div className="log-body">
                  <div className="log-desc">
                    <strong>{e.type}</strong> — {displayNote}
                  </div>
                  <div className="log-meta">{displayMeta.join(" · ")}</div>
                </div>
                <div className="log-qty" style={{ color: cfg.color }}>
                  {e.type === "Vente" || e.type === "SuppressionCommande" ? "−" : "+"}
                  {fmt(e.volumeM3 ?? e.quantite)} m³
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
